import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, MapPin, CalendarDays, Phone, Mail, ExternalLink, Users, Flame,
  Loader2, RefreshCw, Trash2, Home, UserPlus, Check,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  getGarageSale, updateGarageSale, deleteGarageSale, rematchGarageSale,
  registerGarageSaleLead,
} from "../../../api/garageSales";

const TEAL = "#55c5d0";

const auDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

// CG (#12975272406): a lead you click into carries the same three states as the
// lanes — To Work, Assigned, Archived — rather than the older four. The stored
// value maps onto the lane it lands in (in_progress -> To Work lane,
// contacted -> Assigned lane, dismissed -> Archived lane).
const STATUS_OPTIONS = [
  { value: "in_progress", label: "To Work" },
  { value: "contacted", label: "Assigned" },
  { value: "dismissed", label: "Archived" },
];

const Field = ({ label, children }) => (
  <div className="mb-4">
    <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-400 mb-1.5">{label}</div>
    {children}
  </div>
);

const GarageSaleDrawer = ({ saleId, agents = [], onClose, onChanged }) => {
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getGarageSale(saleId)
      .then((res) => {
        if (cancelled) return;
        setSale(res.data);
        setNotes(res.data?.agent_notes || "");
      })
      .catch(() => !cancelled && toast.error("Could not open that garage sale."))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [saleId]);

  // Every write goes through here so the drawer always shows what the server
  // actually stored — the API stamps contacted_at itself, for instance.
  const patch = async (data) => {
    setSaving(true);
    try {
      const res = await updateGarageSale(saleId, data);
      setSale(res.data);
      onChanged?.();
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that change.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Assigning a sale gives that agent a follow-up in their Tasks & Calendar
  // (handled server-side); confirm it so the agent knows the reminder is set.
  const handleAssign = async (e) => {
    const agentId = e.target.value || null;
    const updated = await patch({ assigned_agent: agentId });
    if (updated && agentId && updated.follow_up) {
      toast.success(
        `Follow-up added to ${updated.follow_up.agent_name || "the agent"}'s Tasks & Calendar` +
        (updated.follow_up.due_date ? ` for ${auDate(updated.follow_up.due_date)}.` : ".")
      );
    }
  };

  const handleRematch = async () => {
    setSaving(true);
    try {
      const res = await rematchGarageSale(saleId);
      setSale(res.data.sale);
      toast.success(`${res.data.matched} owner${res.data.matched === 1 ? "" : "s"} found on this street.`);
      onChanged?.();
    } catch {
      toast.error("Could not rebuild the owner matches.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this garage sale?")) return;
    try {
      await deleteGarageSale(saleId);
      toast.success("Garage sale deleted.");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete that sale.");
    }
  };

  // Turn the sale into a Database owner lead the agent can call/door-knock as a
  // potential seller. Idempotent server-side, so a second click just re-confirms.
  const handleRegisterLead = async () => {
    setRegistering(true);
    try {
      const res = await registerGarageSaleLead(saleId);
      setSale(res.data.sale);
      onChanged?.();
      toast.success(res.data.created
        ? "Registered as a potential seller in the Database."
        : "Already registered as a potential seller.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register this lead.");
    } finally {
      setRegistering(false);
    }
  };

  const openMatch = (match) => {
    if (match.match_type === "property" && match.crm_property) {
      navigate(`/properties/${match.crm_property}`);
    } else {
      // Database rows live in the grid, not on their own page.
      navigate("/database");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-400">
              Garage sale
            </div>
            <div className="font-extrabold text-gray-900 truncate">
              {sale?.title || "Garage sale"}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 shrink-0">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {loading || !sale ? (
          <div className="py-20 flex items-center justify-center text-gray-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : (
          <div className="px-5 py-5">
            {/* Why it surfaced — the brief was explicit that the CRM should say
                why someone is appearing, not just that they are. */}
            <div className={`rounded-xl p-3 mb-5 ${
              sale.intent === "high" ? "bg-[#fdecec]" : sale.intent === "medium" ? "bg-[#fdf3e3]" : "bg-[#f4f6f8]"
            }`}>
              <div className="flex items-center gap-1.5 font-extrabold text-[12px] mb-1"
                   style={{ color: sale.intent === "high" ? "#d5453f" : sale.intent === "medium" ? "#c9820f" : "#68727d" }}>
                {sale.intent === "high" && <Flame size={13} />}
                {sale.intent === "high" ? "Looks like a mover"
                  : sale.intent === "medium" ? "Worth a look" : "Ordinary clear-out"}
              </div>
              {sale.intent_reasons?.length > 0 ? (
                <ul className="text-[12px] text-gray-700 list-disc pl-4 space-y-0.5">
                  {sale.intent_reasons.map((r) => <li key={r}>{r}</li>)}
                </ul>
              ) : (
                <div className="text-[12px] text-gray-600">
                  Nothing in the wording suggests a move. Worth a look only if it is on a street you farm.
                </div>
              )}
            </div>

            <Field label="Where and when">
              <div className="text-sm text-gray-800 space-y-1">
                <div className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-gray-400" />
                  {sale.address_raw || [sale.street, sale.suburb].filter(Boolean).join(", ") || "Not published"}
                </div>
                {sale.sale_date && (
                  <div className="flex items-center gap-1.5">
                    <CalendarDays size={14} className="text-gray-400" />
                    {auDate(sale.sale_date)}{sale.sale_time ? ` · ${sale.sale_time}` : ""}
                  </div>
                )}
                <div className="text-[11px] text-gray-400">
                  {sale.source_display}
                  {sale.url && (
                    <a href={sale.url} target="_blank" rel="noopener noreferrer"
                       className="ml-2 inline-flex items-center gap-1 font-bold" style={{ color: TEAL }}>
                      View listing <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
            </Field>

            {sale.description && (
              <Field label="What they are selling">
                <p className="text-[13px] text-gray-700 whitespace-pre-line">{sale.description}</p>
              </Field>
            )}

            {/* Owners on the street. This is the call list. */}
            <Field label={`Owners we know on this street (${sale.matches?.length || 0})`}>
              {sale.matches?.length ? (
                <div className="space-y-2">
                  {sale.matches.map((m) => (
                    <div key={m.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-bold text-sm text-gray-900 truncate">
                            {m.display_name || "Owner name not recorded"}
                          </div>
                          <div className="text-[12px] text-gray-500 truncate">{m.display_address}</div>
                        </div>
                        <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                          {m.confidence === "exact" ? "SAME ADDRESS" : "SAME STREET"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {m.mobile && (
                          <a href={`tel:${m.mobile}`}
                             className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#e9f7ee] text-[#2f9e5d] text-[12px] font-bold">
                            <Phone size={12} /> {m.mobile}
                          </a>
                        )}
                        {m.email && (
                          <a href={`mailto:${m.email}`}
                             className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#eef1fd] text-[#4a5bd0] text-[12px] font-bold truncate max-w-[200px]">
                            <Mail size={12} /> {m.email}
                          </a>
                        )}
                        <button onClick={() => openMatch(m)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 text-[12px] font-bold text-gray-600">
                          {m.match_type === "property" ? <Home size={12} /> : <Users size={12} />}
                          {m.match_type_display}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[12px] text-gray-500">
                  No owner records on this street yet. The street is still worth a door knock or a letter drop.
                </div>
              )}
              <button
                onClick={handleRematch}
                disabled={saving}
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold text-gray-500 hover:text-gray-700 disabled:opacity-60"
              >
                <RefreshCw size={12} /> Re-check this street
              </button>
            </Field>

            <Field label="Potential seller">
              {sale.lead ? (
                <div className="text-[12px] text-gray-600 flex items-center gap-1.5">
                  <Check size={13} className="text-[#2f9e5d]" />
                  Registered as a Database lead
                  {sale.lead.agent_name ? ` for ${sale.lead.agent_name}` : ""} to call.
                </div>
              ) : (
                <>
                  <button
                    onClick={handleRegisterLead}
                    disabled={registering}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white disabled:opacity-60"
                    style={{ background: TEAL }}
                  >
                    {registering ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                    Register as potential seller
                  </button>
                  <p className="text-[11px] text-gray-400 mt-1.5">
                    Adds the street to the Database as an owner to call, having a garage sale.
                  </p>
                </>
              )}
            </Field>

            <Field label="Status">
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => patch({ status: opt.value })}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-bold disabled:opacity-60 ${
                      sale.status === opt.value ? "text-white" : "border border-gray-300 text-gray-600"
                    }`}
                    style={sale.status === opt.value ? { background: TEAL } : undefined}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {sale.contacted_at && (
                <div className="text-[11px] text-gray-400 mt-1.5">
                  Marked contacted {new Date(sale.contacted_at).toLocaleString()}
                </div>
              )}
            </Field>

            <Field label="Assigned to">
              <select
                value={sale.assigned_agent || ""}
                onChange={handleAssign}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              >
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.email}</option>
                ))}
              </select>
              {sale.follow_up && (
                <div className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1.5">
                  <CalendarDays size={12} />
                  Follow-up in {sale.follow_up.agent_name || "the agent"}'s Tasks
                  {sale.follow_up.due_date ? ` · ${auDate(sale.follow_up.due_date)}` : ""}
                </div>
              )}
            </Field>

            <Field label="Notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== (sale.agent_notes || "") && patch({ agent_notes: notes })}
                rows={4}
                placeholder="What happened when you called."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              />
            </Field>

            {sale.source !== "garagesalefinder" && (
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 text-[12px] font-bold text-red-500 hover:text-red-600"
              >
                <Trash2 size={13} /> Delete this sale
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GarageSaleDrawer;
